import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Loader2 } from 'lucide-react';
import { useTenant } from '../../hooks/useTenant';
import { useCemeteryTheme } from '../../hooks/useCemeteryConfig';
import { useVisualViewport } from '../../hooks/useVisualViewport';
import { useCemeteryAIConfig } from '../../hooks/useCemeteryAIConfig';

/**
 * ChatScreen Component
 * Updated: Using Visual Viewport hook with proper flexbox layout
 * Version: 3.0.0
 * Last modified: 2025-01-14T16:00:00.000Z
 * 
 * iOS Safari Optimizations:
 * - Fixed layout foundation prevents document scrolling
 * - Visual Viewport hook handles keyboard detection
 * - Dynamic padding adjusts for keyboard height
 * - Momentum scrolling for smooth iOS touch experience
 * - 16px font size prevents iOS auto-zoom
 * - Proper iOS input attributes for better UX
 */

interface Message {
  id: string;
  content: string;
  timestamp: Date;
  role: 'user' | 'assistant';
  isLoading?: boolean;
}

const ChatScreen: React.FC = () => {
  const { tenant } = useTenant();
  const { data: themeData } = useCemeteryTheme();
  const { data: aiConfig, isLoading: aiConfigLoading, error: aiConfigError } = useCemeteryAIConfig();
  const { keyboardHeight } = useVisualViewport();
  
  // iOS device detection for conditional optimizations
  const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
  
  // Detect if desktop (768px and above)
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  
  // Desktop detection
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // iOS Safari debugging (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && isIOSDevice) {
      console.log('🍎 iOS Safari Debug - Keyboard height changed:', keyboardHeight);
      console.log('🍎 iOS Safari Debug - Keyboard visible:', keyboardHeight > 0);
    }
  }, [keyboardHeight, isIOSDevice]);

  // Get dark mode setting (mobile only)
  const chatDarkMode = themeData?.chat_dark_mode && !isDesktop;
  
  console.log('Full tenant object:', tenant);
  console.log('Theme config:', tenant?.theme_config);
  
  // Extract theme configuration
  const theme = tenant?.themeConfig || tenant?.theme_config;
  const colors = theme?.colors || {
    background: '#ffffff',
    surface: '#f5f5f5',
    border: '#e5e5e5',
    textPrimary: '#000000',
    textSecondary: '#666666',
    accent: tenant?.branding?.primaryColor || '#EA6D2C'
  };

  console.log('🎨 Theme being used:', { theme, colors });
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageListRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const tenantName = tenant?.displayName || tenant?.name || 'Cemetery Services';
  const primaryColor = tenant?.branding?.primaryColor || '#EA6D2C';

  const scrollToBottom = () => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Keyboard-aware auto-scroll: scroll to bottom when messages change or keyboard appears/disappears
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages, keyboardHeight]);

  // Auto-focus on mobile when no messages
  useEffect(() => {
    // Only auto-focus on iOS devices to prevent desktop issues
    if (!isDesktop && isIOSDevice && messages.length === 0) {
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [isDesktop, isIOSDevice, messages.length]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || isLoading) return;
    
    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputMessage.trim(),
      timestamp: new Date(),
      role: 'user'
    };
    
    setMessages(prev => [...prev, userMessage]);
    const currentMessage = inputMessage.trim();
    setInputMessage('');
    setIsLoading(true);
    
    // Add typing indicator
    const typingMessage: Message = {
      id: 'typing',
      content: '',
      timestamp: new Date(),
      role: 'assistant',
      isLoading: true
    };
    setMessages(prev => [...prev, typingMessage]);
    
    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: currentMessage,
          cemetery_id: tenant?.id || 'demo'
        }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      // Remove typing indicator and add actual response
      setMessages(prev => {
        const withoutTyping = prev.filter(msg => msg.id !== 'typing');
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: data.response || 'I apologize, but I\'m having trouble responding right now. Please try again or contact our staff directly.',
          timestamp: new Date(),
          role: 'assistant'
        };
        return [...withoutTyping, assistantMessage];
      });
    } catch (error) {
      console.error('Chat error:', error);
      
      // Remove typing indicator and add error message
      setMessages(prev => {
        const withoutTyping = prev.filter(msg => msg.id !== 'typing');
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: 'I apologize, but I\'m experiencing technical difficulties. Please try again or contact our staff directly for assistance.',
          timestamp: new Date(),
          role: 'assistant'
        };
        return [...withoutTyping, errorMessage];
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(e.target.value);
    
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div 
      className="flex h-full w-full flex-col transition-all duration-200 ease-in-out"
      style={{ 
        backgroundColor: chatDarkMode ? themeData?.chat_bg_color : '#FAFAFA',
        paddingBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px',
      }}
    >
      {/* Messages Container - Let it flex naturally */}
      <div 
        ref={messageListRef}
        className="flex-grow overflow-y-auto momentum-scroll"
        style={{
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {messages.length === 0 ? (
          <div 
            className="flex-1 flex items-center justify-center p-6 min-h-0 cursor-pointer"
            onClick={() => inputRef.current?.focus()}
          >
            <div className="text-center">
              <h2 
                className="text-lg font-light leading-relaxed"
                style={{ 
                  color: chatDarkMode ? themeData?.chat_text_primary : '#374151'
                }}
              >
                {aiConfig?.greeting_message || themeData?.chat_welcome_message || 'How can I assist you today?'}
              </h2>
              <p 
                className="text-sm mt-2 opacity-75"
                style={{ 
                  color: chatDarkMode ? themeData?.chat_text_secondary : '#6B7280'
                }}
              >
                Tap here to start typing
              </p>
            </div>
          </div>
        ) : (
          <div className="px-4 py-6">
            <div className={isDesktop ? 'max-w-3xl mx-auto' : 'w-full'}>
              {messages.map((message, index) => {
                const isUser = message.role === 'user';
                const showSender = index === 0 || messages[index - 1].role !== message.role;
                const isLastMessage = index === messages.length - 1;
                
                return (
                  <div key={message.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'} ${isLastMessage ? 'mb-4' : 'mb-3'}`}>
                    <div className={`max-w-[85%] ${isUser ? 'order-2' : 'order-1'}`}>
                      {showSender && (
                        <div className={`text-xs mb-1 ${isUser ? 'text-right' : 'text-left'}`} style={{ 
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                          color: chatDarkMode ? themeData?.chat_text_secondary : '#6B7280'
                        }}>
                          {isUser ? 'You' : 'Cemetery Assistant'}
                        </div>
                      )}
                      
                      <div
                        className="px-4 py-3 rounded-2xl shadow-sm"
                        style={{
                          backgroundColor: isUser 
                            ? (themeData?.chat_bubble_user_bg || primaryColor)
                            : (chatDarkMode ? themeData?.chat_bubble_assistant_bg : 'white'),
                          color: isUser 
                            ? 'white' 
                            : (chatDarkMode ? themeData?.chat_text_primary : '#1F2937'),
                          borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                          fontSize: '15px',
                          lineHeight: '1.5'
                        }}
                      >
                        {message.isLoading ? (
                          <div className="flex items-center space-x-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span style={{ color: chatDarkMode ? themeData?.chat_text_secondary : '#6B7280' }}>Assistant is typing...</span>
                          </div>
                        ) : (
                          <div className="whitespace-pre-wrap">{message.content}</div>
                        )}
                      </div>
                      
                      {!message.isLoading && (
                        <div className={`text-xs mt-1 opacity-60 ${isUser ? 'text-right' : 'text-left'}`} style={{ 
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                          color: chatDarkMode ? '#6B7280' : '#9CA3AF'
                        }}>
                          {formatTime(message.timestamp)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          </div>
        )}
      </div>

      {/* Input Area - Flex shrink 0 keeps it at bottom */}
      <div 
        className="flex-shrink-0 p-4"
        style={{
          backgroundColor: chatDarkMode ? themeData?.chat_input_bg_color : 'white',
          borderTop: !isDesktop ? 'none' : `1px solid ${chatDarkMode ? themeData?.chat_border_color : '#E5E7EB'}`,
        }}
      >
        <div className={isDesktop ? 'max-w-3xl mx-auto' : 'w-full'}>
          <form onSubmit={handleSubmit} className="relative">
            <div className="flex items-end space-x-2">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  autoFocus={!isDesktop && isIOSDevice}
                  readOnly={false}
                  // iOS-specific input attributes for better UX
                  autoComplete="off"
                  autoCorrect="on"
                  autoCapitalize="sentences"
                  enterKeyHint="send"
                  inputMode="text"
                  tabIndex={0}
                  inputMode="text"
                  tabIndex={0}
                  value={inputMessage}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={isDesktop ? "Type your message..." : "Message..."}
                  disabled={isLoading}
                  rows={1}
                  className="w-full px-4 py-3 pr-14 border rounded-3xl resize-none focus:outline-none focus:ring-2 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed text-base"
                  style={{
                    backgroundColor: chatDarkMode ? themeData?.chat_input_bg_color : undefined,
                    borderColor: chatDarkMode ? themeData?.chat_border_color : '#D1D5DB',
                    color: chatDarkMode ? themeData?.chat_text_primary : undefined,
                    focusRingColor: primaryColor,
                    fontSize: '16px', // Critical: Prevents iOS Safari auto-zoom on focus
                    fontSize: '16px', // Prevents zoom on iOS
                    lineHeight: '1.5'
                  }}
                />
                
                {/* Send Button */}
                <button
                  type="submit"
                  disabled={!inputMessage.trim() || isLoading}
                  className="absolute right-2 bottom-2 w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-95"
                  style={{ backgroundColor: primaryColor }}
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 text-white animate-spin" />
                  ) : (
                    <Send className="w-5 h-5 text-white" />
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChatScreen;